import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import logoWhite from "@/assets/logo-white.png";
import logoBlack from "@/assets/logo-black.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { data: role } = useUserRole(user?.id);
  const isAuthPage = location.pathname === '/auth' || location.pathname === '/area-cliente' || location.pathname.startsWith('/admin');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { label: "Início", href: "#home", isRoute: false },
    { label: "Sobre", href: "#about", isRoute: false },
    { label: "Serviços", href: "#services", isRoute: false },
    { label: "Portfólio", href: "#portfolio", isRoute: false },
    { label: "Blog", href: "#blog", isRoute: false },
    { label: "Contato", href: "#contact", isRoute: false },
  ];

  const handleNavigation = (href: string, isRoute: boolean) => {
    if (isRoute) {
      navigate(href);
      setIsMobileMenuOpen(false);
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(href.replace("#", ""));
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      } else {
        const element = document.getElementById(href.replace("#", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-lg shadow-lg border-b border-border"
          : "bg-transparent"
      }`}
    >
      <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
        <button onClick={() => handleNavigation("#home", false)} className="focus:outline-none">
          <img 
            src={isAuthPage || isScrolled ? logoBlack : logoWhite} 
            alt="Digital Ideia - Soluções Tecnológicas Inteligentes" 
            className="h-14 w-auto object-contain transition-opacity duration-300"
            width="252"
            height="56"
            fetchPriority="high"
          />
        </button>

        {/* Desktop Navigation */}
        <ul className="hidden lg:flex items-center gap-8">
          {menuItems.map((item) => (
            <li key={item.label}>
              <button
                onClick={() => handleNavigation(item.href, item.isRoute)}
                className={`hover:text-primary transition-colors font-medium ${isAuthPage || isScrolled ? 'text-foreground/80' : 'text-white/90'}`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          {role === 'admin' && (
            <Button
              variant="outline"
              onClick={() => navigate("/admin")}
            >
              Painel Admin
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/area-cliente")}
          >
            Área do Cliente
          </Button>
          <Button
            variant="default"
            onClick={() => handleNavigation("#contact", false)}
          >
            Fale Conosco
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden transition-colors ${isAuthPage || isScrolled ? 'text-foreground' : 'text-white'}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border animate-fade-in">
          <ul className="container mx-auto px-4 py-6 flex flex-col gap-4">
            {menuItems.map((item) => (
              <li key={item.label}>
                <button
                  onClick={() => handleNavigation(item.href, item.isRoute)}
                  className="text-foreground/80 hover:text-primary transition-colors font-medium w-full text-left py-2"
                >
                  {item.label}
                </button>
              </li>
            ))}
            {role === 'admin' && (
              <li>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/admin")}
                >
                  Painel Admin
                </Button>
              </li>
            )}
            <li>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/area-cliente")}
              >
                Área do Cliente
              </Button>
            </li>
            <li>
              <Button
                variant="default"
                className="w-full"
                onClick={() => handleNavigation("#contact", false)}
              >
                Fale Conosco
              </Button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
